import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Check, GripVertical } from 'lucide-react';

import { api } from '../../api/client.js';
import './DropdownsPanel.css';

// The 8 dropdown lists we manage. Each one is a JSON array under its settings key.
const DROPDOWN_DEFS = [
    { key: 'sectors',          title: 'Sectors',          description: 'Industry sectors for investors and startups.' },
    { key: 'investor_types',   title: 'Investor types',   description: 'Categories of investors (Angel, Seed VC, etc.).' },
    { key: 'financing_types',  title: 'Financing types',  description: 'Forms of financing offered.' },
    { key: 'investment_stages',title: 'Investment stages',description: 'Company stages investors focus on.' },
    { key: 'deal_stages',      title: 'Deal stages',      description: 'Kanban board columns for the deal pipeline.' },
    { key: 'status_options',   title: 'Status options',   description: 'Investor status values (Hot, Warm, Cold, etc.).' },
    { key: 'fit_levels',       title: 'Fit levels',       description: 'How well an investor matches a portfolio company.' },
    { key: 'geo_options',      title: 'Geographic focus', description: 'Regions investors cover.' },
];

export function DropdownsPanel() {
    const queryClient = useQueryClient();
    const { data: settings, isLoading, error } = useQuery({
        queryKey: ['settings'],
        queryFn: () => api.get('/settings'),
    });

    const saveMutation = useMutation({
        mutationFn: ({ key, value }) => api.put(`/settings/${key}`, { value }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
    });

    if (isLoading) {
        return <div className="panel-empty"><span className="spinner" /></div>;
    }

    if (error) {
        return <div className="panel-error">Couldn't load settings: {error.message}</div>;
    }

    return (
        <div>
            {DROPDOWN_DEFS.map(def => {
                const values = Array.isArray(settings?.[def.key]) ? settings[def.key] : [];
                return (
                    <DropdownEditor
                        key={def.key}
                        def={def}
                        values={values}
                        onSave={(newValues) => saveMutation.mutate({ key: def.key, value: newValues })}
                        saving={saveMutation.isPending && saveMutation.variables?.key === def.key}
                    />
                );
            })}
        </div>
    );
}

/* ==========================================================================
 * DropdownEditor — one card per dropdown list
 * ========================================================================== */
function DropdownEditor({ def, values, onSave, saving }) {
    const [newValue, setNewValue] = useState('');
    const [editingIdx, setEditingIdx] = useState(null);
    const [editValue, setEditValue] = useState('');

    const handleAdd = () => {
        const v = newValue.trim();
        if (!v) return;
        if (values.includes(v)) return;  // no duplicates
        onSave([...values, v]);
        setNewValue('');
    };

    const handleDelete = (idx) => {
        onSave(values.filter((_, i) => i !== idx));
    };

    const handleEditStart = (idx) => {
        setEditingIdx(idx);
        setEditValue(values[idx]);
    };

    const handleEditSave = () => {
        const v = editValue.trim();
        if (!v) { setEditingIdx(null); return; }
        const next = [...values];
        next[editingIdx] = v;
        onSave(next);
        setEditingIdx(null);
    };

    const move = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= values.length) return;
        const next = [...values];
        [next[idx], next[target]] = [next[target], next[idx]];
        onSave(next);
    };

    return (
        <div className="panel-section">
            <div className="panel-section-header">
                <div>
                    <h2 className="panel-section-title">{def.title}</h2>
                    <p className="panel-section-subtitle">{def.description}</p>
                </div>
                {saving && <span className="dropdown-saving mono">Saving…</span>}
            </div>

            {values.length === 0 && (
                <div className="dropdown-empty">No options yet. Add one below.</div>
            )}

            <ul className="dropdown-list">
                {values.map((v, idx) => (
                    <li key={`${v}-${idx}`} className="dropdown-item">
                        <button className="dropdown-drag"
                                onMouseDown={() => move(idx, 0)}
                                disabled
                                title="Reorder (use arrow buttons below for now)">
                            <GripVertical size={12} strokeWidth={1.75} />
                        </button>

                        {editingIdx === idx ? (
                            <>
                                <input
                                    className="dropdown-edit-input"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleEditSave();
                                        if (e.key === 'Escape') setEditingIdx(null);
                                    }}
                                    autoFocus
                                />
                                <button className="dropdown-btn dropdown-btn-accept"
                                        onClick={handleEditSave} title="Save">
                                    <Check size={13} strokeWidth={2} />
                                </button>
                                <button className="dropdown-btn" onClick={() => setEditingIdx(null)}
                                        title="Cancel">
                                    <X size={13} strokeWidth={2} />
                                </button>
                            </>
                        ) : (
                            <>
                                <button className="dropdown-value"
                                        onClick={() => handleEditStart(idx)}>
                                    {v}
                                </button>
                                <button className="dropdown-btn" onClick={() => move(idx, -1)}
                                        disabled={idx === 0} title="Move up">↑</button>
                                <button className="dropdown-btn" onClick={() => move(idx, 1)}
                                        disabled={idx === values.length - 1}
                                        title="Move down">↓</button>
                                <button className="dropdown-btn dropdown-btn-danger"
                                        onClick={() => handleDelete(idx)} title="Delete">
                                    <X size={13} strokeWidth={2} />
                                </button>
                            </>
                        )}
                    </li>
                ))}
            </ul>

            <div className="dropdown-add">
                <input
                    type="text"
                    className="dropdown-add-input"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
                    placeholder={`Add a new ${def.title.toLowerCase().replace(/s$/, '')}...`}
                />
                <button className="dropdown-add-btn" onClick={handleAdd} disabled={!newValue.trim()}>
                    <Plus size={14} strokeWidth={2} /> Add
                </button>
            </div>
        </div>
    );
}
